import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { comics } from '@/mocks/comics';
import ComicCard from '@/pages/home/components/ComicCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const comic = useMemo(() => {
    return comics.find(c => c.id === slug) || null;
  }, [slug]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter(c => c.id !== comic.id && c.category.some(cat => comic.category.includes(cat)))
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 6);
  }, [comic]);

  if (!comic) {
    return (
      <div className="pt-28 pb-20 text-center bg-background-50 min-h-screen flex flex-col items-center justify-center">
        <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5 rounded-full bg-background-100">
          <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
        <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已下架</p>
        <Link
          to="/"
          className="px-6 py-2.5 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://www.koreanmanhwa.cn/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": `${comic.categoryNames[0]}漫画`,
        "item": `https://www.koreanmanhwa.cn/category/${comic.category[0]}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": comic.title,
        "item": `https://www.koreanmanhwa.cn/article/${comic.id}`
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${comic.title} - 韩国漫画网`,
    "description": comic.description,
    "author": { "@type": "Person", "name": comic.author },
    "datePublished": "2023-01-15",
    "dateModified": comic.updateDate,
    "url": `https://www.koreanmanhwa.cn/article/${comic.id}`
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <header className="pt-24 pb-8 md:pt-28 md:pb-10 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${comic.category[0]}`} className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              {comic.categoryNames[0]}漫画
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800">{comic.title}</span>
          </nav>
        </div>
      </header>

      <section className="py-6 md:py-10 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-[300px] flex-shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
                <img
                  src={comic.coverUrl}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-2">
                作者：<span className="text-foreground-700">{comic.author}</span>
              </p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full whitespace-nowrap">
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-star-fill text-yellow-500"></i>
                  <strong>{comic.rating}</strong> 分
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-eye-line"></i>
                  {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 次阅读
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-file-list-3-line"></i>
                  共 <strong>{comic.chapters}</strong> 话
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-5">
                {comic.categoryNames.map(name => (
                  <Link
                    key={name}
                    to={`/category/${comic.category[comic.categoryNames.indexOf(name)]}`}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {name}
                  </Link>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-5">
                {comic.tags.map(tag => (
                  <span key={tag} className="px-2.5 py-0.5 bg-background-100 text-foreground-500 text-xs rounded-full whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-5 mb-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-3">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer">
                  开始阅读第一话
                </button>
                <button className="px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer border border-background-200/70">
                  <i className="ri-bookmark-line mr-1.5"></i>加入收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-2">章节列表</h2>
          <p className="text-sm text-foreground-500 mb-6">共 {comic.chapters} 话 · {comic.status}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: Math.min(comic.chapters, 30) }).map((_, index) => (
              <button
                key={index}
                className="px-4 py-3 text-sm text-foreground-700 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 hover:text-primary-600 transition-colors text-left whitespace-nowrap cursor-pointer"
              >
                第 {comic.chapters - index} 话
              </button>
            ))}
          </div>
          {comic.chapters > 30 && (
            <div className="text-center mt-6">
              <button className="px-6 py-2.5 bg-background-50 border border-background-200/70 text-foreground-600 text-sm rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                展开更多章节
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
            </div>
          )}
        </div>
      </section>

      {relatedComics.length > 0 && (
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="inline-block px-4 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
                  相关推荐
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  喜欢这部的人<span className="text-primary-600">也在看</span>
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
              {relatedComics.map(item => (
                <ComicCard key={item.id} comic={item} size="small" />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}